import { useCallback, useEffect, useRef } from "react";
import {
  Brain,
  Check,
  FileText,
  FolderSearch,
  GitFork,
  Image as ImageIcon,
  ListChecks,
  ListOrdered,
  Loader2,
  PenLine,
  Route,
  Search,
  Sparkles,
  User,
  type LucideIcon,
} from "lucide-react";
import { AssistantMarkdownContent } from "@/components/chat/AssistantMarkdownContent";
import { cn } from "@/lib/utils";
import type { RagStreamStatus } from "../api/types.js";
import type { OnOpenCitationDocument } from "../lib/open-citation-document.js";
import type { RagCitation, RagMessage } from "../types.js";
import { MessageSourcesFooter } from "./MessageSourcesFooter.js";
import { RagMessageActions } from "./RagMessageActions.js";

/** Pixels from bottom to consider the user "following" new content. */
const SCROLL_BOTTOM_THRESHOLD_PX = 48;

function isNearBottom(el: HTMLElement): boolean {
  const { scrollTop, scrollHeight, clientHeight } = el;
  const noScrollNeeded = scrollHeight <= clientHeight + 2;
  return (
    noScrollNeeded || scrollTop + clientHeight >= scrollHeight - SCROLL_BOTTOM_THRESHOLD_PX
  );
}

const assistantBubbleClassName =
  "min-w-0 rounded-2xl rounded-tl-sm border border-[rgba(15,23,42,0.09)] bg-white px-4 py-3 text-[13px] leading-relaxed text-[#101828] shadow-[var(--shadow-card)] dark:border-white/10 dark:bg-white/10 dark:text-white dark:shadow-none";

const userBubbleClassName =
  "min-w-0 rounded-2xl rounded-tr-sm border border-[var(--bubble-user-border)] bg-[var(--bubble-user-bg)] px-4 py-3 text-[13px] leading-relaxed text-[var(--bubble-user-fg)] dark:border-transparent dark:bg-primary dark:text-white";

interface ChatMessagesProps {
  messages: RagMessage[];
  activeCitationId?: string | null;
  onCitationClick?: (citation: RagCitation) => void;
  threadId?: string;
  onOpenDocument?: OnOpenCitationDocument;
  isThinking?: boolean;
  thinkingSteps?: RagStreamStatus[];
  busy?: boolean;
  onRegenerate?: () => void;
  onFeedbackChange?: (messageId: string, rating: RagMessage["feedback"]) => void;
}

function UserAvatar() {
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--avatar-user-border)] bg-[var(--avatar-user-bg)] dark:border-transparent dark:bg-primary">
      <User className="h-3.5 w-3.5 text-[var(--avatar-user-fg)] dark:text-white" />
    </div>
  );
}

function AssistantAvatar() {
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--avatar-assistant-border)] bg-[var(--avatar-assistant-bg)] dark:border-transparent dark:bg-gradient-to-br dark:from-blue-500 dark:to-purple-600">
      <Sparkles className="h-3.5 w-3.5 text-[var(--avatar-assistant-fg)] dark:text-white" />
    </div>
  );
}

function MessageBubble({
  message,
  activeCitationId,
  onCitationClick,
  threadId,
  onOpenDocument,
  anchorRef,
  busy,
  showRegenerate,
  onRegenerate,
  onFeedbackChange,
}: {
  message: RagMessage;
  activeCitationId?: string | null;
  onCitationClick?: (citation: RagCitation) => void;
  threadId?: string;
  onOpenDocument?: OnOpenCitationDocument;
  anchorRef?: React.Ref<HTMLDivElement>;
  busy?: boolean;
  showRegenerate?: boolean;
  onRegenerate?: () => void;
  onFeedbackChange?: (messageId: string, rating: RagMessage["feedback"]) => void;
}) {
  const isUser = message.role === "user";
  const isStreaming = message.id.startsWith("streaming-");
  const isLast = Boolean(anchorRef);
  const citations = isUser ? [] : (message.citations ?? []);

  if (isUser) {
    return (
      <div
        ref={isLast ? anchorRef : undefined}
        data-role="user"
        className="ml-auto flex max-w-[85%] scroll-mt-6 flex-row-reverse gap-2.5"
      >
        <UserAvatar />
        <div className="flex min-w-0 flex-col items-end">
          <div className={userBubbleClassName}>
            <span className="break-words whitespace-pre-wrap">{message.content}</span>
          </div>
          <RagMessageActions
            messageId={message.id}
            content={message.content}
            variant="user"
            busy={busy}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      ref={isLast ? anchorRef : undefined}
      data-role="assistant"
      className="mr-auto flex max-w-[85%] scroll-mt-6 gap-2.5"
    >
      <AssistantAvatar />
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className={cn(assistantBubbleClassName, "space-y-3")}>
          <AssistantMarkdownContent
            content={message.content}
            citations={isStreaming ? undefined : message.citations}
            activeCitationId={activeCitationId}
            onCitationClick={onCitationClick}
            threadId={threadId}
            onOpenDocument={onOpenDocument}
            isStreaming={isStreaming}
          />
          {citations.length > 0 ? (
            <MessageSourcesFooter
              citations={citations}
              activeCitationId={activeCitationId}
              onCitationClick={onCitationClick}
            />
          ) : null}
        </div>
        {!isStreaming ? (
          <RagMessageActions
            messageId={message.id}
            content={message.content}
            variant="assistant"
            busy={busy}
            onRegenerate={showRegenerate ? onRegenerate : undefined}
            feedbackRating={message.feedback ?? null}
            onFeedbackChange={
              onFeedbackChange
                ? (rating) => onFeedbackChange(message.id, rating)
                : undefined
            }
          />
        ) : null}
      </div>
    </div>
  );
}

const SLOW_STEP_THRESHOLD_MS = 5000;

const STAGE_ICONS: Record<RagStreamStatus["stage"], LucideIcon> = {
  analyzing: Brain,
  inspecting: FolderSearch,
  routing: Route,
  planning: ListChecks,
  retrieving_subqueries: GitFork,
  visual_search: ImageIcon,
  searching: Search,
  ranking: ListOrdered,
  preparing: FileText,
  generating: PenLine,
};

function formatStepLabel(step: RagStreamStatus, isActive: boolean): string {
  if (!isActive || !step.elapsedMs || step.elapsedMs < SLOW_STEP_THRESHOLD_MS) {
    return step.message;
  }
  return `${step.message} (${Math.round(step.elapsedMs / 1000)}s)`;
}

function ThinkingBubble({
  anchorRef,
  steps,
}: {
  anchorRef: React.Ref<HTMLDivElement>;
  steps: RagStreamStatus[];
}) {
  const activeIndex = steps.length - 1;

  return (
    <div
      ref={anchorRef}
      data-role="assistant"
      className="mr-auto flex max-w-[85%] scroll-mt-6 gap-2.5"
    >
      <AssistantAvatar />
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className={assistantBubbleClassName}>
          {steps.length === 0 ? (
            <div className="flex items-center gap-2 text-[#576274] dark:text-gray-300">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Thinking...</span>
            </div>
          ) : (
            <ul className="flex flex-col gap-1.5" aria-live="polite">
              {steps.map((step, index) => {
                const isActive = index === activeIndex;
                const StageIcon = STAGE_ICONS[step.stage] ?? Search;
                return (
                  <li
                    key={step.stage}
                    className={cn(
                      "flex items-center gap-2",
                      isActive
                        ? "text-[#101828] dark:text-white"
                        : "text-[#576274]/70 dark:text-gray-400",
                    )}
                  >
                    <StageIcon
                      className={cn(
                        "h-4 w-4 shrink-0",
                        isActive && "animate-pulse text-blue-500 dark:text-blue-400",
                      )}
                    />
                    <span>{formatStepLabel(step, isActive)}</span>
                    {isActive ? (
                      <Loader2 className="ml-auto h-3.5 w-3.5 shrink-0 animate-spin" />
                    ) : (
                      <Check className="ml-auto h-3.5 w-3.5 shrink-0" />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export function ChatMessages({
  messages,
  activeCitationId,
  onCitationClick,
  threadId,
  onOpenDocument,
  isThinking = false,
  thinkingSteps = [],
  busy = false,
  onRegenerate,
  onFeedbackChange,
}: ChatMessagesProps) {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const shouldStickToBottomRef = useRef(true);
  const isProgrammaticScrollRef = useRef(false);
  const prevScrollTopRef = useRef(0);
  const lastMessageAnchorRef = useRef<HTMLDivElement>(null);

  const handleMessagesScroll = useCallback(() => {
    if (isProgrammaticScrollRef.current) return;
    const el = messagesContainerRef.current;
    if (!el) return;
    const { scrollTop } = el;
    const delta = scrollTop - prevScrollTopRef.current;
    if (Math.abs(delta) > 4 && !isNearBottom(el)) {
      shouldStickToBottomRef.current = false;
    } else if (isNearBottom(el)) {
      shouldStickToBottomRef.current = true;
    }
    prevScrollTopRef.current = scrollTop;
  }, []);

  const scrollLastMessageIntoView = useCallback(() => {
    lastMessageAnchorRef.current?.scrollIntoView({
      block: "start",
      behavior: "auto",
    });
  }, []);

  useEffect(() => {
    if (isThinking) {
      shouldStickToBottomRef.current = true;
    }
  }, [isThinking]);

  useEffect(() => {
    if (messages.length === 0 && !isThinking) return;
    if (!shouldStickToBottomRef.current) return;

    isProgrammaticScrollRef.current = true;
    scrollLastMessageIntoView();
    const t = window.setTimeout(() => {
      scrollLastMessageIntoView();
      window.setTimeout(() => {
        isProgrammaticScrollRef.current = false;
        const el = messagesContainerRef.current;
        if (el) prevScrollTopRef.current = el.scrollTop;
      }, 0);
    }, 0);
    return () => clearTimeout(t);
  }, [messages, isThinking, scrollLastMessageIntoView]);

  return (
    <div
      ref={messagesContainerRef}
      onScroll={handleMessagesScroll}
      className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto"
    >
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-3 py-3 sm:px-5 sm:py-5">
        {messages.map((message, index) => {
          const isLastMessage = index === messages.length - 1 && !isThinking;
          const isLastAssistant =
            message.role === "assistant" && isLastMessage && Boolean(onRegenerate);

          return (
            <MessageBubble
              key={message.id}
              message={message}
              activeCitationId={activeCitationId}
              onCitationClick={onCitationClick}
              threadId={threadId}
              onOpenDocument={onOpenDocument}
              anchorRef={isLastMessage ? lastMessageAnchorRef : undefined}
              busy={busy}
              showRegenerate={isLastAssistant}
              onRegenerate={onRegenerate}
              onFeedbackChange={onFeedbackChange}
            />
          );
        })}
        {isThinking ? (
          <ThinkingBubble anchorRef={lastMessageAnchorRef} steps={thinkingSteps} />
        ) : null}
      </div>
    </div>
  );
}
